import React, { useMemo } from 'react';
import styled from 'styled-components';
import Routes from 'routes';

import Icon from 'components/elements/Icon';
import { TileList } from 'shared/Tile';

import {
  DateDetail,
  DisclosureDetail,
  IdentifierDetail,
  PrimaryDetail,
  SecondaryDetail,
  CenteredSecondaryDetail,
} from './Detail';

import { TraceabilityDetails } from './Details';
import type { IcoTraceability } from '../types/traceability';
import TypeBadge from './TypeBadge';
import placeholder from 'utils/placeholder';
import { weightToString } from 'utils/weight';
import ActionStack from './ActionStack';

const TextWithEllipsis = styled.div`
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;
`;

const ContractDetails = styled.div`
  display: grid;

  & > div {
    display: flex;
    justify-content: space-between;
    overflow-x: hidden;
  }
`;

const GridDetails = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  grid-gap: 0.5em;
`;

export interface IcoTraceabilityHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  onCollapsibleHandler: (event: React.MouseEvent) => any;
  collapsed: boolean;
  traceability: IcoTraceability;
}

function TraceabilityHeader({
  traceability: {
    id,
    assigned_coffees,
    type,
    country,
    created_at,
    grade,
    contract: { identifier, certificate, mark, price, customer },
    quality,
    weight,
    local_identifier,
    alternative_ico_id,
    production_order_identifier,
    milling_order_identifier,
    export_date,
    export_identifier,
    units,
  },
  collapsed,
  onCollapsibleHandler,
  ...props
}: IcoTraceabilityHeaderProps) {
  const format_secondary_ico = useMemo(() => {
    let string = local_identifier;
    if (alternative_ico_id) {
      string += '\n' + alternative_ico_id;
    }
    return string;
  }, [local_identifier, alternative_ico_id]);
  return (
    <TileList {...props}>
      <TraceabilityDetails>
        <TypeBadge type={type}>{type}</TypeBadge>

        <DateDetail>{created_at}</DateDetail>

        <PrimaryDetail data-cy='traceability_quality' align='center'>
          {placeholder(quality)}
        </PrimaryDetail>

        <PrimaryDetail data-cy='traceability_weight' align='right'>
          {weightToString(weight)}
        </PrimaryDetail>

        <DisclosureDetail align='center'>
          {assigned_coffees.length > 0 && (
            <Icon
              onClick={onCollapsibleHandler}
              icon={collapsed ? 'caret-down' : 'caret-up'}
              className={'selected-lots__disclosure-icon'}
            />
          )}
        </DisclosureDetail>

        <IdentifierDetail>{format_secondary_ico}</IdentifierDetail>

        <CenteredSecondaryDetail align='center'>
          {placeholder(grade)}
        </CenteredSecondaryDetail>

        <SecondaryDetail align='right' data-cy='traceability_number_of_bags'>
          {I18n.translate('attributes.units', { count: units })}
        </SecondaryDetail>
      </TraceabilityDetails>

      <ContractDetails>
        <div>
          <TextWithEllipsis>{identifier}</TextWithEllipsis>
          <TextWithEllipsis>{placeholder(certificate)}</TextWithEllipsis>
          <TextWithEllipsis>{`${price.value} ${price.currency}`}</TextWithEllipsis>
        </div>

        <div>
          <TextWithEllipsis>{customer}</TextWithEllipsis>
          <TextWithEllipsis>{placeholder(mark)}</TextWithEllipsis>
        </div>
      </ContractDetails>

      <GridDetails>
        <TextWithEllipsis>{placeholder(production_order_identifier)}</TextWithEllipsis>
        <TextWithEllipsis>{placeholder(milling_order_identifier)}</TextWithEllipsis>
        <TextWithEllipsis>{placeholder(export_identifier)}</TextWithEllipsis>
        <TextWithEllipsis>{placeholder(export_date)}</TextWithEllipsis>
        <ActionStack>
          <a
            href={Routes.print_qr_ico_path(id)}
            target='_blank'
            rel='noreferrer'
            className='button button--small button--ghost'
          >
            {I18n.translate('traceability.print_qr')}
          </a>
          {country === 'Nicaragua' && (
            <a
              href={Routes.print_liquidation_information_ico_path(id)}
              target='_blank'
              rel='noreferrer'
              className='button button--small button--ghost'
            >
              {I18n.translate('fixed_price_contracts.pending.print')}
            </a>
          )}
        </ActionStack>
      </GridDetails>
    </TileList>
  );
}

export default TraceabilityHeader;
